import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { apiClient } from "../../services/apiClient";
import LoadingSpinner from "../common/LoadingSpinner";
import ErrorMessage from "../common/ErrorMessage";

const ModelContent = () => {
  const [documents, setDocuments] = useState([]);
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showUploadImageModal, setShowUploadImageModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [documentsSearchTerm, setDocumentsSearchTerm] = useState("");
  const [imagesSearchTerm, setImagesSearchTerm] = useState("");

  useEffect(() => {
    loadDocuments();
    loadImages();
  }, []);

  const loadDocuments = async () => {
    try {
      setLoading(true);
      setError("");
      const response = await apiClient.getModelContentDocuments();
      if (response.success) {
        setDocuments(response.data.documents || []);
      }
    } catch (err) {
      setError(err.message || "Failed to load model content documents");
    } finally {
      setLoading(false);
    }
  };

  const loadImages = async () => {
    try {
      const response = await apiClient.getModelContentImages();
      if (response.success) {
        setImages(response.data.images || []);
      }
    } catch (err) {
      console.error("Failed to load images:", err);
    }
  };

  const handleUploadSuccess = (documentId) => {
    setShowUploadModal(false);
    loadDocuments();

    // Start polling to update processing status
    if (documentId) {
      startPollingForProcessing(documentId);
    }
  };

  const handleImageUploadSuccess = (imageId) => {
    setShowUploadImageModal(false);
    loadImages();

    // Start polling to update processing status
    if (imageId) {
      startPollingForImageProcessing(imageId);
    }
  };

  const startPollingForProcessing = (documentId) => {
    const pollInterval = setInterval(async () => {
      try {
        const response = await apiClient.getModelContentDocuments();

        if (response.success) {
          const docs = response.data.documents || [];
          setDocuments(docs);

          const doc = docs.find(d => d.document_id === documentId);

          // Stop polling if document is completed or failed
          if (doc && (doc.processing_status === 'completed' || doc.processing_status === 'failed')) {
            clearInterval(pollInterval);

            if (doc.processing_status === 'completed') {
              // Optional: Show success notification
              console.log('Model content document processing completed!');
            } else {
              console.error('Model content document processing failed');
            }
          }
        }
      } catch (err) {
        console.error('Polling error:', err);
      }
    }, 2000); // Poll every 2 seconds

    // Stop polling after 60 seconds max
    setTimeout(() => clearInterval(pollInterval), 60000);
  };

  const startPollingForImageProcessing = (imageId) => {
    const pollInterval = setInterval(async () => {
      try {
        const response = await apiClient.getModelContentImages();

        if (response.success) {
          const imgs = response.data.images || [];
          setImages(imgs);

          const img = imgs.find(i => i.document_id === imageId);

          // Stop polling if image is completed or failed
          if (img && (img.processing_status === 'completed' || img.processing_status === 'failed')) {
            clearInterval(pollInterval);

            if (img.processing_status === 'completed') {
              console.log('Model content image processing completed!');
            } else {
              console.error('Model content image processing failed');
            }
          }
        }
      } catch (err) {
        console.error('Polling error:', err);
      }
    }, 2000); // Poll every 2 seconds

    // Stop polling after 60 seconds max
    setTimeout(() => clearInterval(pollInterval), 60000);
  };

  const handleEditClick = (document) => {
    setSelectedDocument(document);
    setShowEditModal(true);
  };

  const handleEditSuccess = () => {
    setShowEditModal(false);
    setSelectedDocument(null);
    loadDocuments();
  };

  const handleDelete = async (documentId, documentName) => {
    if (!window.confirm(`Are you sure you want to delete "${documentName}"?`)) {
      return;
    }

    try {
      await apiClient.deleteModelContentDocument(documentId);
      loadDocuments();
    } catch (err) {
      alert(`Failed to delete document: ${err.message}`);
    }
  };

  const handleDeleteImage = async (imageId, imageName) => {
    if (!window.confirm(`Are you sure you want to delete "${imageName}"?`)) {
      return;
    }

    try {
      await apiClient.deleteModelContentImage(imageId);
      loadImages();
    } catch (err) {
      alert(`Failed to delete image: ${err.message}`);
    }
  };

  const handleDownload = async (documentId, filename) => {
    try {
      await apiClient.downloadModelContentDocument(documentId, filename);
    } catch (err) {
      alert(`Failed to download document: ${err.message}`);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { bg: "bg-gray-100", text: "text-gray-700", icon: "clock" },
      processing: { bg: "bg-blue-100", text: "text-blue-700", icon: "spinner" },
      completed: { bg: "bg-green-100", text: "text-green-700", icon: "check-circle" },
      failed: { bg: "bg-red-100", text: "text-red-700", icon: "exclamation-circle" },
    };

    const config = statusConfig[status] || statusConfig.pending;

    return (
      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
        <FontAwesomeIcon icon={config.icon} className={`mr-1 ${status === 'processing' ? 'animate-spin' : ''}`} />
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const filteredDocuments = documents.filter(doc =>
    doc.document_name?.toLowerCase().includes(documentsSearchTerm.toLowerCase()) ||
    doc.description?.toLowerCase().includes(documentsSearchTerm.toLowerCase()) ||
    doc.original_filename?.toLowerCase().includes(documentsSearchTerm.toLowerCase())
  );

  const filteredImages = images.filter(img =>
    img.document_name?.toLowerCase().includes(imagesSearchTerm.toLowerCase()) ||
    img.description?.toLowerCase().includes(imagesSearchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingSpinner size="lg" text="Loading model content..." />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h3 className="text-lg font-semibold text-slate-900">Model Content</h3>
        <p className="text-sm text-slate-600 mt-1">
          Upload exemplar documents and images to reference in AI-powered section generation
        </p>
      </div>

      {error && <ErrorMessage message={error} onRetry={loadDocuments} />}

      {/* Model Content Documents Section */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-slate-900">
            <FontAwesomeIcon icon="file-contract" className="mr-2 text-red-500" />
            Model Content Documents ({filteredDocuments.length})
          </h2>
          <div className="flex items-center space-x-3">
            <input
              type="text"
              placeholder="Search documents..."
              value={documentsSearchTerm}
              onChange={(e) => setDocumentsSearchTerm(e.target.value)}
              className="px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 text-sm"
            />
            <button
              onClick={() => setShowUploadModal(true)}
              className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors flex items-center space-x-2"
            >
              <FontAwesomeIcon icon="plus" />
              <span>Upload Document</span>
            </button>
          </div>
        </div>

        {filteredDocuments.length === 0 ? (
          <div className="text-center py-12 bg-slate-50 rounded-lg border-2 border-dashed border-slate-300">
            <FontAwesomeIcon icon="file-contract" className="text-4xl text-slate-300 mb-4" />
            <h3 className="text-lg font-medium text-slate-900 mb-2">
              {documentsSearchTerm ? "No documents found" : "No model content documents yet"}
            </h3>
            <p className="text-slate-600 mb-6">
              {documentsSearchTerm
                ? "Try adjusting your search terms"
                : "Upload exemplar documents to use as reference material for AI generation"
              }
            </p>
            {!documentsSearchTerm && (
              <button
                onClick={() => setShowUploadModal(true)}
                className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
              >
                <FontAwesomeIcon icon="plus" className="mr-2" />
                Upload Document
              </button>
            )}
          </div>
        ) : (
          <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-slate-200">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                      Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                      AI Instruction
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                      File
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                      Uploaded
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-slate-200">
                  {filteredDocuments.map((doc) => (
                    <tr key={doc.document_id} className="hover:bg-slate-50">
                      <td className="px-6 py-4">
                        <div className="text-sm font-medium text-slate-900">
                          {doc.document_name}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-slate-600 max-w-xs truncate">
                          {doc.description || <span className="italic text-slate-400">No AI instruction</span>}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-slate-600">
                          {doc.original_filename}
                        </div>
                        <div className="text-xs text-slate-400">
                          {(doc.file_size / 1024).toFixed(1)} KB
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getStatusBadge(doc.processing_status)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500">
                        <div>{new Date(doc.created_at).toLocaleDateString()}</div>
                        <div className="text-xs text-slate-400">
                          by {doc.uploaded_by_username}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-3">
                        <button
                          onClick={() => handleEditClick(doc)}
                          className="text-blue-600 hover:text-blue-900"
                          title="Edit"
                        >
                          <FontAwesomeIcon icon="edit" />
                        </button>
                        <button
                          onClick={() => handleDownload(doc.document_id, doc.original_filename)}
                          className="text-green-600 hover:text-green-900"
                          title="Download"
                        >
                          <FontAwesomeIcon icon="download" />
                        </button>
                        <button
                          onClick={() => handleDelete(doc.document_id, doc.document_name)}
                          className="text-red-600 hover:text-red-900"
                          title="Delete"
                        >
                          <FontAwesomeIcon icon="trash" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Model Content Images Section */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-slate-900">
            <FontAwesomeIcon icon="image" className="mr-2 text-purple-500" />
            Model Content Images ({filteredImages.length})
          </h2>
          <div className="flex items-center space-x-3">
            <input
              type="text"
              placeholder="Search images..."
              value={imagesSearchTerm}
              onChange={(e) => setImagesSearchTerm(e.target.value)}
              className="px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm"
            />
            <button
              onClick={() => setShowUploadImageModal(true)}
              className="px-4 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition-colors flex items-center space-x-2"
            >
              <FontAwesomeIcon icon="plus" />
              <span>Upload Image</span>
            </button>
          </div>
        </div>

        {filteredImages.length === 0 ? (
          <div className="text-center py-12 bg-slate-50 rounded-lg border-2 border-dashed border-slate-300">
            <FontAwesomeIcon icon="image" className="text-4xl text-slate-300 mb-4" />
            <h3 className="text-lg font-medium text-slate-900 mb-2">
              {imagesSearchTerm ? "No images found" : "No model content images yet"}
            </h3>
            <p className="text-slate-600 mb-6">
              {imagesSearchTerm
                ? "Try adjusting your search terms"
                : "Upload exemplar images to use as reference material for AI generation"
              }
            </p>
            {!imagesSearchTerm && (
              <button
                onClick={() => setShowUploadImageModal(true)}
                className="px-4 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition-colors"
              >
                <FontAwesomeIcon icon="plus" className="mr-2" />
                Upload Image
              </button>
            )}
          </div>
        ) : (
          <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-slate-200">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                      Preview
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                      Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                      Description
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                      File
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                      Uploaded
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-slate-200">
                  {filteredImages.map((img) => (
                    <tr key={img.document_id} className="hover:bg-slate-50">
                      <td className="px-6 py-4">
                        {img.processing_status === 'completed' && (
                          <img
                            src={apiClient.getModelContentImageUrl(img.document_id)}
                            alt={img.document_name}
                            className="w-16 h-16 object-cover rounded border border-slate-200"
                            onError={(e) => {
                              e.target.style.display = 'none';
                            }}
                          />
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm font-medium text-slate-900">
                          {img.document_name}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-slate-600 max-w-xs truncate">
                          {img.description || <span className="italic text-slate-400">No description</span>}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-slate-600">
                          {img.original_filename}
                        </div>
                        <div className="text-xs text-slate-400">
                          {(img.file_size / 1024).toFixed(1)} KB
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getStatusBadge(img.processing_status)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500">
                        <div>{new Date(img.created_at).toLocaleDateString()}</div>
                        <div className="text-xs text-slate-400">
                          by {img.uploaded_by_username}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-3">
                        <button
                          onClick={() => handleEditClick(img)}
                          className="text-blue-600 hover:text-blue-900"
                          title="Edit"
                        >
                          <FontAwesomeIcon icon="edit" />
                        </button>
                        <button
                          onClick={() => handleDeleteImage(img.document_id, img.document_name)}
                          className="text-red-600 hover:text-red-900"
                          title="Delete"
                        >
                          <FontAwesomeIcon icon="trash" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Upload Document Modal */}
      {showUploadModal && (
        <UploadModal
          onClose={() => setShowUploadModal(false)}
          onSuccess={handleUploadSuccess}
        />
      )}

      {/* Upload Image Modal */}
      {showUploadImageModal && (
        <UploadImageModal
          onClose={() => setShowUploadImageModal(false)}
          onSuccess={handleImageUploadSuccess}
        />
      )}

      {/* Edit Modal */}
      {showEditModal && selectedDocument && (
        <EditModal
          document={selectedDocument}
          onClose={() => {
            setShowEditModal(false);
            setSelectedDocument(null);
          }}
          onSuccess={handleEditSuccess}
        />
      )}
    </div>
  );
};

// Upload Modal Component
const UploadModal = ({ onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    documentName: "",
    description: ""
  });
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
      // Auto-populate document name from filename if empty
      if (!formData.documentName) {
        const nameWithoutExt = selectedFile.name.replace(/\.[^/.]+$/, "");
        setFormData({ ...formData, documentName: nameWithoutExt });
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!file) {
      setError("Please select a file");
      return;
    }

    if (!formData.documentName.trim()) {
      setError("Please enter a document name");
      return;
    }

    try {
      setUploading(true);
      setError("");

      const response = await apiClient.uploadModelContentDocument(
        file,
        formData.documentName,
        formData.description
      );

      if (response.success && response.data.documentId) {
        onSuccess(response.data.documentId);
      } else {
        onSuccess();
      }
    } catch (err) {
      setError(err.message || "Failed to upload document");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-2xl mx-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-slate-900">
            Upload Model Content Document
          </h3>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600"
          >
            <FontAwesomeIcon icon="times" />
          </button>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* File Upload */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Document File *
            </label>
            <input
              type="file"
              accept=".doc,.docx,.pdf"
              onChange={handleFileChange}
              className="w-full"
              disabled={uploading}
            />
            <p className="text-xs text-slate-500 mt-1">
              Supported formats: Word (.doc, .docx) and PDF
            </p>
            {file && (
              <div className="mt-2 text-sm text-slate-600">
                Selected: {file.name} ({(file.size / 1024).toFixed(1)} KB)
              </div>
            )}
          </div>

          {/* Document Name */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Document Name *
            </label>
            <input
              type="text"
              value={formData.documentName}
              onChange={(e) => setFormData({ ...formData, documentName: e.target.value })}
              placeholder="e.g., Model Executive Summary for LMS Products"
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
              disabled={uploading}
            />
            <p className="text-xs text-slate-500 mt-1">
              Give this document a descriptive name to help identify it later
            </p>
          </div>

          {/* AI Instruction */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              AI Instruction <span className="text-slate-500 font-normal">(Optional)</span>
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Example: If an Executive Summary section is required, use this content as the template but tweak it to focus on the specific customer information in the RFP..."
              rows={3}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
              disabled={uploading}
            />
            <p className="text-xs text-slate-500 mt-1">
              Instructions for the AI on when and how to use this model content during proposal generation
            </p>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-slate-700 hover:text-slate-900"
              disabled={uploading}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={uploading || !file}
              className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {uploading ? (
                <>
                  <FontAwesomeIcon icon="spinner" className="animate-spin mr-2" />
                  Uploading...
                </>
              ) : (
                <>
                  <FontAwesomeIcon icon="upload" className="mr-2" />
                  Upload Document
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Edit Modal Component
const EditModal = ({ document, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    documentName: document.document_name || "",
    description: document.description || ""
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.documentName.trim()) {
      setError("Document name is required");
      return;
    }

    try {
      setSaving(true);
      setError("");

      await apiClient.updateModelContentDocument(
        document.document_id,
        formData.documentName,
        formData.description
      );

      onSuccess();
    } catch (err) {
      setError(err.message || "Failed to update document");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-2xl mx-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-slate-900">
            Edit Model Content Document
          </h3>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600"
          >
            <FontAwesomeIcon icon="times" />
          </button>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Document Name *
            </label>
            <input
              type="text"
              value={formData.documentName}
              onChange={(e) => setFormData({ ...formData, documentName: e.target.value })}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
              disabled={saving}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              AI Instruction <span className="text-slate-500 font-normal">(Optional)</span>
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Example: If an Executive Summary section is required, use this content as the template but tweak it to focus on the specific customer information in the RFP..."
              rows={3}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
              disabled={saving}
            />
            <p className="text-xs text-slate-500 mt-1">
              Instructions for the AI on when and how to use this model content during proposal generation
            </p>
          </div>

          <div className="flex items-center justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-slate-700 hover:text-slate-900"
              disabled={saving}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 disabled:opacity-50"
            >
              {saving ? "Saving..." : "Save Changes"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Upload Image Modal Component
const UploadImageModal = ({ onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    imageName: "",
    description: ""
  });
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      // Validate file type
      const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/svg+xml', 'image/webp'];
      if (!validTypes.includes(selectedFile.type)) {
        setError('Please select a valid image file (JPG, PNG, GIF, SVG, or WebP)');
        return;
      }

      // Validate file size (20MB)
      if (selectedFile.size > 20 * 1024 * 1024) {
        setError('Image file size must be less than 20MB');
        return;
      }

      setFile(selectedFile);
      setError("");

      // Auto-populate image name from filename if empty
      if (!formData.imageName) {
        const nameWithoutExt = selectedFile.name.replace(/\.[^/.]+$/, "");
        setFormData({ ...formData, imageName: nameWithoutExt });
      }

      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreview(reader.result);
      };
      reader.readAsDataURL(selectedFile);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!file) {
      setError("Please select an image file");
      return;
    }

    if (!formData.imageName.trim()) {
      setError("Please enter an image name");
      return;
    }

    try {
      setUploading(true);
      setError("");

      const response = await apiClient.uploadModelContentImage(
        file,
        formData.imageName,
        formData.description
      );

      if (response.success && response.data.imageId) {
        onSuccess(response.data.imageId);
      } else {
        onSuccess();
      }
    } catch (err) {
      setError(err.message || "Failed to upload image");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-2xl mx-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-slate-900">
            Upload Model Content Image
          </h3>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600"
          >
            <FontAwesomeIcon icon="times" />
          </button>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* File Upload */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Image File *
            </label>
            <input
              type="file"
              accept="image/jpeg,image/jpg,image/png,image/gif,image/svg+xml,image/webp"
              onChange={handleFileChange}
              className="w-full"
              disabled={uploading}
            />
            <p className="text-xs text-slate-500 mt-1">
              Supported formats: JPG, PNG, GIF, SVG, WebP (max 20MB)
            </p>
            {preview && (
              <div className="mt-4">
                <p className="text-sm font-medium text-slate-700 mb-2">Preview:</p>
                <img
                  src={preview}
                  alt="Preview"
                  className="max-w-full h-auto max-h-64 rounded border border-slate-300"
                />
                <p className="text-xs text-slate-500 mt-2">
                  {file.name} ({(file.size / 1024).toFixed(1)} KB)
                </p>
              </div>
            )}
          </div>

          {/* Image Name */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Image Name *
            </label>
            <input
              type="text"
              value={formData.imageName}
              onChange={(e) => setFormData({ ...formData, imageName: e.target.value })}
              placeholder="e.g., Organizational Chart 2024"
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
              disabled={uploading}
            />
            <p className="text-xs text-slate-500 mt-1">
              Give this image a descriptive name to help identify it later
            </p>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Optional: Describe when and how this image should be used..."
              rows={3}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
              disabled={uploading}
            />
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-slate-700 hover:text-slate-900"
              disabled={uploading}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={uploading || !file}
              className="px-4 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {uploading ? (
                <>
                  <FontAwesomeIcon icon="spinner" className="animate-spin mr-2" />
                  Uploading...
                </>
              ) : (
                <>
                  <FontAwesomeIcon icon="upload" className="mr-2" />
                  Upload Image
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ModelContent;
